import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import type Result from 'true-myth/result';

import { ApplicationContext } from '~/shared/context/context';
import type { FilterRequest } from '~/shared/data-contract/filter';
import type {
    JobProfilesOptionsResponse,
    JobProfilesPermissions,
    JobProfilesResponse,
} from '~/shared/data-contract/job-profile';
import { jobProfilesOptionsResponseSchema, jobProfilesResponseSchema } from '~/shared/data-contract/job-profile';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

const optionsPathname: Pathname = '/job-profiles/options';

const filterPathname: Pathname = `/job-profiles/filter`;

export const useFilterJobProfiles = (
    { filter, paged = { page: 0, size: 20 }, sorted }: FilterRequest,
    enabled = true,
): UseQueryResult<JobProfilesResponse, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(jobProfilesResponseSchema, {
                pathname: filterPathname,
                payload: {
                    filter,
                    paged,
                    sorted,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [filterPathname, 'postJson', jobProfilesResponseSchema, filter, paged, sorted],
    });
};

export type JobProfilesOptionsResult = UseQueryResult<Result<JobProfilesOptionsResponse, Error>>;

export const useJobProfilesOptionsQuery = (): JobProfilesOptionsResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async () =>
            validatingCobrainerApiFetcher.get(jobProfilesOptionsResponseSchema, {
                pathname: optionsPathname,
            }),
        queryKey: [optionsPathname, 'get', jobProfilesOptionsResponseSchema],
        staleTime: 10_000,
    });
};

export const useJobProfilesPermissions = (): JobProfilesPermissions & {
    readonly isPending: boolean;
} => {
    const { data, isPending } = useJobProfilesOptionsQuery();

    if (data?.isOk === true) {
        return {
            canUserGetJobProfiles: data.value.readMany.enabled,
            isPending,
        };
    }

    return {
        canUserGetJobProfiles: false,
        isPending,
    };
};
